import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';

import { Box, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import FileUploadContainer from '../FormField/FileUploadContainer';
import ImagesUploadContainer from '../FormField/ImagesUploadContainer';

import CheckboxComponent from '../FormField/CheckboxComponent';
import CheckboxGroupComponent from '../FormField/CheckboxGroupComponent';
import InfoMessageComponent from '../FormField/InfoMessageComponent';
import NumberFieldComponent from '../FormField/NumberFieldComponent';
import OneLineTextFieldComponent from '../FormField/OneLineTextFieldComponent';
import PasswordFieldComponent from '../FormField/PasswordFieldComponent';
import PhoneNumber from '../FormField/PhoneNumberComponent';
import SingleDatePickerComponent from '../FormField/SingleDatePickerComponent';
import StyledLinkComponent from '../FormField/StyledLinkComponent';
import TextFieldComponent from '../FormField/TextFieldComponent';
import TimePickerComponent from '../FormField/TimePickerComponent';
import UploadPreviewComponent from '../FormField/UploadPreviewComponent';
import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import ReactSelectInputComponent from '../Select/SelectComponent';
import ButtonSelectComponent from './ButtonSelectComponent';
import GeolocationFieldComponent from './GeolocationFieldComponent';

import { getCharacterLimit } from './utils';

import ChipTextField from '../FormField/ChipTextField';
import CustomPickerRow from '../FormField/CustomPickerRow';
import DateTimePicker from '../FormField/DateTimePicker';
import MultipleTextFields from '../FormField/MultipleTextFields';
import PhoneNumberWithExtension from '../FormField/PhoneNumberWithExtension';
import PhoneNumberWithVerification from '../FormField/PhoneNumberWithVerification';
import ScheduleContainer from '../Schedule';
import SpreadsheetContainer from '../Spreadsheet';
import {
  BUTTON_SELECT_COMPONENT,
  CHECKBOX,
  CHECKBOX_GROUP,
  CHIP_TEXT_FIELD,
  CUSTOM_FIELD_COMPONENT,
  CUSTOM_PICKER_ROW,
  DATE_PICKER,
  DATE_TIME_PICKER,
  EXPANDABLE_IMAGE_COMPONENT,
  FILE_UPLOAD,
  GEO,
  IMAGE_PREVIEW,
  IMAGE_UPLOAD,
  MESSAGE,
  MULTIPLE_TEXT_FIELDS,
  ONE_LINE_TEXT_FIELD,
  PHONE_NUMBER,
  PHONE_NUMBER_WITH_EXTENSION,
  PHONE_NUMBER_WITH_VERIFICATION,
  REACT_SELECT_INPUT,
  SCHEDULE,
  SPREADSHEET_UPLOAD,
  STYLED_LINK,
  STYLED_NUMBER,
  STYLED_PASSWORD,
  STYLED_TEXT,
  TIME_PICKER,
  VISITOR_DATE_PICKER,
} from './fieldTypes';

const FormFieldComponent = (props) => {
  const {
    isHidden,
    fieldType,
    validate,
    keyName,
    label,
    isLoading,
    isSkeletonDisabled,
    isTouched,
    // for MESSAGE
    value,
    // for EXPANDABLE_IMAGE_COMPONENT
    src,
    height,
    isSquare,
    // for CUSTOM_FIELD_COMPONENT
    children,
  } = props;

  if (isHidden) return null;

  const showSkeleton = isLoading && !isTouched && !isSkeletonDisabled;

  let customProps = {};
  let component;
  let isLabelHidden = Boolean(!label);

  switch (fieldType) {
    case STYLED_TEXT:
      component = TextFieldComponent;
      customProps = pick(props, [
        'autoComplete',
        'autoFocus',
        'color',
        'defaultValue',
        'disabled',
        'fullWidth',
        'helperText',
        'id',
        'placeholder',
        'size',
        'multiline',
        'rows',
        'maxRows',
        'minRows',
        'fieldNotes',
        'normalize',
        'type',
        'InputProps',
      ]);
      customProps.characterLimit = getCharacterLimit(validate);
      break;
    case CHIP_TEXT_FIELD:
      component = ChipTextField;
      customProps = pick(props);
      break;
    case STYLED_NUMBER: // for visitor settings
      component = NumberFieldComponent;
      customProps = pick(props, [
        'color',
        'defaultValue',
        'disabled',
        'fullWidth',
        'helperText',
        'id',
        'placeholder',
        'size',
        'normalize',
        'parse',
        'pattern',
        'textBefore',
        'textAfter',
      ]);
      isLabelHidden = true;
      break;
    case STYLED_PASSWORD:
      component = PasswordFieldComponent;
      customProps = pick(props, [
        'autoComplete',
        'autoFocus',
        'color',
        'defaultValue',
        'disabled',
        'fullWidth',
        'helperText',
        'id',
        'placeholder',
        'size',
        'onPaste',
        'onCopy',
        'showPasswordRequirements',
      ]);
      break;
    case REACT_SELECT_INPUT:
      component = ReactSelectInputComponent;
      customProps = pick(props, [
        'options',
        'disabled',
        'multi',
        'infoMessage',
        'showInfoMessage',
        'isClearable',
        'placeholder',
        'sortAlphabetically',
      ]);
      break;
    case BUTTON_SELECT_COMPONENT:
      component = ButtonSelectComponent;
      customProps = pick(props, ['options']);
      break;
    case MESSAGE:
      return <InfoMessageComponent value={value} />;
    case GEO:
      component = GeolocationFieldComponent;
      break;
    case IMAGE_PREVIEW:
      component = UploadPreviewComponent;
      customProps = pick(props, [
        'url',
        'onRemoveItem',
        'height',
        'isSquare',
        'isMulti',
      ]);
      break;
    case IMAGE_UPLOAD: {
      component = ImagesUploadContainer;
      customProps = pick(props, ['disabled', 'isMulti', 'isReadOnly']);
      break;
    }
    case FILE_UPLOAD: {
      component = FileUploadContainer;
      customProps = pick(props, ['disabled', 'isMulti', 'accept']);
      break;
    }
    case SPREADSHEET_UPLOAD: {
      component = SpreadsheetContainer;
      customProps = pick(props, [
        'onImport',
        'onFileUpload',
        'onImportTitle',
        'isEditable',
      ]);
      break;
    }
    case VISITOR_DATE_PICKER:
    case DATE_PICKER:
      component = SingleDatePickerComponent;
      customProps = pick(props, [
        'id',
        'disabled',
        'disablePast',
        'shouldBePermanent',
        'showCalendarIcon',
        'disableOneYearRange',
      ]);
      break;
    case TIME_PICKER:
      component = TimePickerComponent;
      customProps = pick(props, [
        'disabled',
        'disablePast', // mobile picker only
        'getDisabledHours',
        'getDisabledMinutes',
        'timeFormat',
        'use12Hours',
        'minuteStep',
        'isMobilePicker',
      ]);
      break;
    case DATE_TIME_PICKER:
      component = DateTimePicker;
      customProps = pick(props, [
        'disabled',
        'disabledTime',
        'disablePast',
        'onChange',
        'onChangeFormatter',
        'shouldDisableDate',
        'getDisabledHours',
        'getDisabledMinutes',
        'style',
        'showTimePicker',
        'setReservationDate',
      ]);
      break;
    case CUSTOM_PICKER_ROW:
      component = CustomPickerRow;
      customProps = pick(props, [
        'options',
        'disabled',
        'getDisabledHours',
        'getDisabledMinutes',
        'minuteStep',
        'selectedAmenity',
        'reservationDate',
        'setSelectedStartTime',
        'setSelectedDuration',
        'selectedStartTime',
        'selectedDuration',
        'isStartTime',
        'isDayTimePickerDisabled',
        'avoidShowingTimePicker',
        'fullWidthPicker',
        'isCreate',
      ]);
      break;
    case STYLED_LINK:
      component = StyledLinkComponent;
      customProps = pick(props, ['amenities']);
      break;
    case CHECKBOX:
      component = CheckboxComponent;
      customProps = pick(props, ['label', 'disabled', 'headerLabel']);
      isLabelHidden = true; // label for a checkbox is not displayed above but to the right
      break;
    case CHECKBOX_GROUP:
      component = CheckboxGroupComponent;
      customProps = pick(props, ['label', 'disabled', 'options']);
      isLabelHidden = true;
      break;
    case EXPANDABLE_IMAGE_COMPONENT:
      return (
        <Box mb="1rem">
          <ExpandableImageComponent
            src={src}
            height={height}
            caption={label}
            isSquare={isSquare}
          />
        </Box>
      );
    case PHONE_NUMBER:
      component = PhoneNumber;
      customProps = pick(props, [
        'disabled',
        'showClearPhoneNumber',
        'isFullWidth',
        'placeholder',
      ]);
      break;
    case SCHEDULE:
      component = ScheduleContainer;
      customProps = pick(props, [
        'isMultiTimeSlot',
        'getDisabledScheduleValues',
        'timezone',
        'formErrors',
      ]);
      break;

    case ONE_LINE_TEXT_FIELD:
      component = OneLineTextFieldComponent;
      customProps = pick(props, ['label', 'disabled', 'placeholder']);
      isLabelHidden = true;
      break;
    case PHONE_NUMBER_WITH_EXTENSION:
      component = PhoneNumberWithExtension;
      customProps = pick(props, [
        'disabled',
        'showClearPhoneNumber',
        'isFullWidth',
        'onFieldChange',
      ]);
      isLabelHidden = true;
      break;
    case PHONE_NUMBER_WITH_VERIFICATION:
      component = PhoneNumberWithVerification;
      customProps = pick(props, [
        'disabled',
        'showClearPhoneNumber',
        'isFullWidth',
        'onFieldChange',
        'verificationStatus',
        'buildingId',
      ]);
      break;
    case MULTIPLE_TEXT_FIELDS:
      component = MultipleTextFields;
      customProps = pick(props, [
        'autoComplete',
        'autoFocus',
        'color',
        'defaultValue',
        'disabled',
        'fullWidth',
        'helperText',
        'id',
        'placeholder',
        'size',
        'multiline',
        'rows',
        'maxRows',
        'minRows',
        'fieldNotes',
        'normalize',
        'type',
        'InputProps',
        'values',
        'isCreate',
        'isLoading',
        'isWiFiLayout',
      ]);
      customProps.characterLimit = getCharacterLimit(validate);
      break;
    case CUSTOM_FIELD_COMPONENT:
      return (
        <div style={{ marginBottom: '1.5rem' }}>
          <Typography variant="body1">{label}</Typography>
          {children(props)}
        </div>
      );
    default:
      break;
  }

  // TODO: add default validation for dates

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {!isLabelHidden && (
        <Typography variant="body1" component="div" display="flex">
          {label}
        </Typography>
      )}
      {showSkeleton ? (
        <Skeleton height="4rem" />
      ) : (
        <Field
          name={keyName}
          component={component}
          {...customProps}
          validate={validate}
        />
      )}
    </div>
  );
};

FormFieldComponent.propTypes = {
  fieldType: PropTypes.string.isRequired,
  keyName: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSkeletonDisabled: PropTypes.bool.isRequired,
  isTouched: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  validate: PropTypes.instanceOf(Array),
  isHidden: PropTypes.bool,
  children: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  src: PropTypes.string,
  height: PropTypes.string,
  isSquare: PropTypes.bool,
};

FormFieldComponent.defaultProps = {
  validate: [],
  isHidden: false,
  isTouched: false,
  children: () => {},
  label: null,
};

export default FormFieldComponent;
